// src/components/common/vibe/PollCard.js
import React, { useState, useEffect } from 'react';
import { Check, Clock, Users, BarChart3, Trophy, XCircle, CheckCircle, Edit } from 'lucide-react';
import vibeService from '../../../services/vibe.service';
import EditPoll from './EditPoll';

const PollCard = ({ post, currentUser, onVoted, theme }) => {
    const [voting, setVoting] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [pollData, setPollData] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    // Load detailed poll data on mount
    useEffect(() => {
        const loadPollData = async () => {
            try {
                const response = await vibeService.getPoll(post.id);
                setPollData(response.data);
                if (response.data.voted_option_index !== null) {
                    setSelectedOption(response.data.voted_option_index);
                }
            } catch (error) {
                console.error('Error loading poll data:', error);
            }
        };
        loadPollData();
    }, [post.id]);

    // Use pollData if available, otherwise fallback to post data
    const poll = pollData || post;

    // Parse poll options
    const pollOptions = poll.poll_options ?
        (typeof poll.poll_options === 'string' ? JSON.parse(poll.poll_options) : poll.poll_options)
        : [];

    // Check if poll has ended
    const hasEnded = poll.poll_is_ended || (poll.poll_end_date && new Date(poll.poll_end_date) < new Date());

    // Check if user has voted
    const hasVoted = poll.has_voted || selectedOption !== null;

    // Calculate total votes
    const totalVotes = poll.total_votes || pollOptions.reduce((sum, opt) => sum + (opt.votes || 0), 0);

    // Handle vote
    const handleVote = async (optionIndex) => {
        if (hasEnded || voting || !poll.can_vote) return;

        setVoting(true);
        try {
            await vibeService.votePoll(post.id, optionIndex);
            setSelectedOption(optionIndex);
            // Reload poll data to get updated vote counts
            const response = await vibeService.getPoll(post.id);
            setPollData(response.data);
            if (onVoted) onVoted();
        } catch (error) {
            console.error('Error voting on poll:', error);
            alert(error.response?.data?.message || 'Failed to vote. Please try again.');
        } finally {
            setVoting(false);
        }
    };

    // Handle end poll
    const handleEndPoll = async () => {
        if (!window.confirm('Are you sure you want to end this poll? This cannot be undone.')) return;

        setProcessing(true);
        try {
            await vibeService.endPoll(post.id);
            const response = await vibeService.getPoll(post.id);
            setPollData(response.data);
            if (onVoted) onVoted();
        } catch (error) {
            console.error('Error ending poll:', error);
            alert('Failed to end poll. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    // Handle publish results
    const handlePublishResults = async () => {
        setProcessing(true);
        try {
            await vibeService.publishPollResults(post.id);
            const response = await vibeService.getPoll(post.id);
            setPollData(response.data);
            if (onVoted) onVoted();
        } catch (error) {
            console.error('Error publishing results:', error);
            alert('Failed to publish results. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    // Get percentage for option
    const getPercentage = (votes) => {
        if (totalVotes === 0) return 0;
        return Math.round((votes / totalVotes) * 100);
    };

    // Get winning option index
    const getWinningOptionIndex = () => {
        if (poll.poll_winning_option_index !== null && poll.poll_winning_option_index !== undefined) {
            return poll.poll_winning_option_index;
        }
        if (pollOptions.length === 0) return null;
        let maxVotes = 0;
        let winningIndex = null;
        pollOptions.forEach((option, index) => {
            if ((option.votes || 0) > maxVotes) {
                maxVotes = option.votes || 0;
                winningIndex = index;
            }
        });
        return winningIndex;
    };

    const winningOptionIndex = getWinningOptionIndex();

    // Handle poll update
    const handlePollUpdated = async () => {
        const response = await vibeService.getPoll(post.id);
        setPollData(response.data);
        if (onVoted) onVoted();
    };

    return (
        <>
            {/* Edit Poll Modal */}
            <EditPoll
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                poll={poll}
                onPollUpdated={handlePollUpdated}
                theme={theme}
            />

            <div className={`rounded-xl p-6 border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                {(poll.creator_name || poll.author_name || '')?.split(' ').map(n => n[0]).join('') || '?'}
                            </div>
                            <div>
                                <p className={`font-medium text-sm ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    {poll.creator_name || poll.author_name}
                                </p>
                                <p className={`text-xs ${
                                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`}>
                                    {new Date(poll.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Poll Status Badge */}
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    hasEnded
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                }`}>
                    {hasEnded ? '🔒 Ended' : '🗳️ Active'}
                </div>
            </div>

            {/* Title */}
            {poll.poll_title && (
                <h2 className={`text-xl font-bold mb-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    {poll.poll_title}
                </h2>
            )}

            {/* Description */}
            {poll.poll_description && (
                <p className={`text-sm mb-3 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                    {poll.poll_description}
                </p>
            )}

            {/* Question */}
            <h3 className={`text-lg font-semibold mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
                📊 {poll.poll_question || poll.content}
            </h3>

            {/* Poll Status */}
            <div className="flex items-center gap-4 mb-4 text-sm">
                <div className={`flex items-center gap-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                    <Users className="w-4 h-4" />
                    {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
                </div>
                {post.poll_end_date && (
                    <div className={`flex items-center gap-1 ${
                        hasEnded
                            ? 'text-red-600 dark:text-red-400'
                            : theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        <Clock className="w-4 h-4" />
                        {hasEnded ? 'Ended' : 'Ends'} {new Date(post.poll_end_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                    </div>
                )}
                {hasEnded && (
                    <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-xs font-medium">
                        Poll Closed
                    </span>
                )}
            </div>

            {/* Poll Options */}
            <div className="space-y-3">
                {pollOptions.map((option, index) => {
                    const percentage = getPercentage(option.votes || 0);
                    const isSelected = selectedOption === index;
                    const isWinner = poll.poll_result_published && winningOptionIndex === index;
                    const showResults = hasVoted || hasEnded || (poll.can_view_results && totalVotes > 0);
                    // Allow voting even if already voted (to change vote), but not if poll has ended
                    const canVote = poll.can_vote && !hasEnded;

                    return (
                        <div key={index} className="relative">
                            {canVote && !hasVoted ? (
                                /* Vote Button (Not Voted Yet) */
                                <button
                                    onClick={() => handleVote(index)}
                                    disabled={voting}
                                    className={`w-full px-4 py-3 rounded-lg border-2 text-left font-medium transition-all ${
                                        isSelected
                                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                            : voting
                                                ? 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                                                : theme === 'dark'
                                                    ? 'border-gray-700 hover:border-blue-600 hover:bg-gray-700 text-white'
                                                    : 'border-gray-200 hover:border-blue-600 hover:bg-blue-50 text-gray-900'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span>{option.text || option}</span>
                                        {isSelected && (
                                            <Check className="w-5 h-5" />
                                        )}
                                    </div>
                                </button>
                            ) : canVote && hasVoted ? (
                                /* Results with Change Vote Button (Voted, Can Change) */
                                <button
                                    onClick={() => handleVote(index)}
                                    disabled={voting}
                                    className={`w-full relative overflow-hidden rounded-lg border transition-all ${
                                        voting ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'
                                    } ${
                                        isSelected
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                            : isWinner
                                            ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                                            : theme === 'dark'
                                            ? 'border-gray-700 bg-gray-750 hover:border-blue-500'
                                            : 'border-gray-200 bg-gray-50 hover:border-blue-500'
                                    }`}
                                >
                                    {/* Progress Bar Background */}
                                    <div
                                        className={`absolute inset-0 transition-all ${
                                            isWinner
                                                ? 'bg-yellow-200 dark:bg-yellow-700/30'
                                                : 'bg-blue-100 dark:bg-blue-800/20'
                                        }`}
                                        style={{ width: `${percentage}%` }}
                                    />

                                    {/* Option Content */}
                                    <div className="relative z-10 px-4 py-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2">
                                                {isSelected && <Check className="w-5 h-5 text-blue-600" />}
                                                {isWinner && <Trophy className="w-5 h-5 text-yellow-600" />}
                                                <span className={`font-medium ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {option.text || option}
                                                </span>
                                                {isSelected && (
                                                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                                        (Your vote - click to change)
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className={`text-sm ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    {option.votes || 0} {(option.votes || 0) === 1 ? 'vote' : 'votes'}
                                                </span>
                                                <span className={`font-bold ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {percentage}%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ) : showResults ? (
                                /* Results Display with Progress Bar */
                                <div className={`relative overflow-hidden rounded-lg border ${
                                    isSelected
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                        : isWinner
                                        ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                                        : theme === 'dark'
                                        ? 'border-gray-700 bg-gray-750'
                                        : 'border-gray-200 bg-gray-50'
                                }`}>
                                    {/* Progress Bar Background */}
                                    <div
                                        className={`absolute inset-0 transition-all ${
                                            isWinner
                                                ? 'bg-yellow-200 dark:bg-yellow-700/30'
                                                : 'bg-blue-100 dark:bg-blue-800/20'
                                        }`}
                                        style={{ width: `${percentage}%` }}
                                    />

                                    {/* Option Content */}
                                    <div className="relative z-10 px-4 py-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2">
                                                {isSelected && <Check className="w-5 h-5 text-blue-600" />}
                                                {isWinner && <Trophy className="w-5 h-5 text-yellow-600" />}
                                                <span className={`font-medium ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {option.text || option}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className={`text-sm ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    {option.votes || 0} {(option.votes || 0) === 1 ? 'vote' : 'votes'}
                                                </span>
                                                <span className={`font-bold ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {percentage}%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* Hidden option - user can't vote or see results */
                                <div className={`px-4 py-3 rounded-lg border ${
                                    theme === 'dark' ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
                                }`}>
                                    <span className={`font-medium ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                        {option.text || option}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Voting Status */}
            {voting && (
                <div className="mt-4 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg">
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Recording your vote...
                    </div>
                </div>
            )}

            {/* Thank You Message */}
            {hasVoted && !hasEnded && !poll.poll_result_published && (
                <div className="mt-4 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg text-sm">
                        <Check className="w-4 h-4" />
                        Thanks for voting! You can change your vote anytime before the poll ends.
                    </div>
                </div>
            )}

            {/* Results Published Banner */}
            {poll.poll_result_published && (
                <div className="mt-4 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 rounded-lg text-sm font-medium">
                        <Trophy className="w-4 h-4" />
                        Final results published
                        {winningOptionIndex !== null && pollOptions[winningOptionIndex] && (
                            <span>- Winner: {pollOptions[winningOptionIndex].text || pollOptions[winningOptionIndex]}</span>
                        )}
                    </div>
                </div>
            )}

            {/* Permission Denied Message */}
            {!poll.can_vote && !hasVoted && !hasEnded && (
                <div className="mt-4 text-center">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
                        theme === 'dark'
                            ? 'bg-gray-700 text-gray-300'
                            : 'bg-gray-100 text-gray-700'
                    }`}>
                        🔒 You don't have permission to vote in this poll
                    </div>
                </div>
            )}

            {/* Admin Controls */}
            {(poll.can_edit || poll.can_end_poll || poll.can_publish_results) && (
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex flex-wrap gap-2">
                        {poll.can_edit && !hasEnded && (
                            <button
                                onClick={() => setShowEditModal(true)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                title="Edit poll settings"
                            >
                                <Edit className="w-4 h-4" />
                                Edit Poll
                            </button>
                        )}

                        {poll.can_end_poll && !hasEnded && (
                            <button
                                onClick={handleEndPoll}
                                disabled={processing}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                                    processing
                                        ? 'bg-red-400 cursor-not-allowed'
                                        : 'bg-red-600 hover:bg-red-700'
                                } text-white`}
                            >
                                <XCircle className="w-4 h-4" />
                                {processing ? 'Ending...' : 'End Poll'}
                            </button>
                        )}

                        {poll.can_publish_results && hasEnded && !poll.poll_result_published && (
                            <button
                                onClick={handlePublishResults}
                                disabled={processing}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                                    processing
                                        ? 'bg-green-400 cursor-not-allowed'
                                        : 'bg-green-600 hover:bg-green-700'
                                } text-white`}
                            >
                                <CheckCircle className="w-4 h-4" />
                                {processing ? 'Publishing...' : 'Publish Results'}
                            </button>
                        )}
                    </div>

                    {/* Admin Info */}
                    <p className={`text-xs mt-3 ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                        Admin controls • Total votes: {totalVotes} •
                        {poll.poll_show_results_to && ` Visibility: ${poll.poll_show_results_to.replace('_', ' ')}`}
                    </p>
                </div>
            )}
            </div>
        </>
    );
};

export default PollCard;
